/**
 * preferencesHelpers.js — RC 1.43.B Unified Preference Hydration (Frontend).
 *
 * Pure helpers shared between:
 *   • PreferencesCard.js (Settings → Preferences editor)
 *   • JobsPage.js        (hydration banner)
 *
 * Mirrors the canonical defaults from the backend
 * `core.candidate_preferences.DEFAULT_PREFERENCES`. Drift between
 * the two is caught by `preferencesHelpers.test.js`.
 *
 * No React imports here — these are framework-free utilities.
 */

export const PREFERENCE_DEFAULTS = {
  // Pre-1.43.A keys.
  preferred_regions:         [],
  open_to_relocation:        'conditional',
  relocation_regions:        [],
  location_strictness:       'balanced',
  include_cover_letter:      true,
  discoverable_to_employers: false,
  // RC 1.43.A new keys (backend-canonical).
  preferred_titles:          [],
  industries:                [],
  excluded_keywords:         [],
  employment_types:          [],
  seniority_targets:         [],
  remote_preferences:        'any',
  salary_floor:              null,
  salary_ceiling:            null,
  salary_currency:           'GBP',
  salary_period:             'annual',
};

// Keys whose value is a list. Compared element-wise.
export const ARRAY_KEYS = [
  'preferred_regions',
  'relocation_regions',
  'preferred_titles',
  'industries',
  'excluded_keywords',
  'employment_types',
  'seniority_targets',
];

// Backend-mirrored enum vocabularies. Drift here is caught by the
// test that checks every enum option round-trips through the diff.
export const EMPLOYMENT_TYPE_OPTIONS = [
  { value: 'full_time',  label: 'Full time' },
  { value: 'part_time',  label: 'Part time' },
  { value: 'contract',   label: 'Contract' },
  { value: 'temp',       label: 'Temp / Temporary' },
  { value: 'internship', label: 'Internship' },
];

export const SENIORITY_OPTIONS = [
  { value: 'intern',   label: 'Intern' },
  { value: 'junior',   label: 'Junior' },
  { value: 'mid',      label: 'Mid' },
  { value: 'senior',   label: 'Senior' },
  { value: 'lead',     label: 'Lead' },
  { value: 'director', label: 'Director' },
  { value: 'vp',       label: 'VP' },
  { value: 'c_suite',  label: 'C-Suite' },
];

export const REMOTE_PREF_OPTIONS = [
  { value: 'any',          label: 'Any — no preference' },
  { value: 'remote_only',  label: 'Remote only' },
  { value: 'hybrid',       label: 'Hybrid' },
  { value: 'onsite_only',  label: 'On-site only' },
];

export const SALARY_CURRENCY_OPTIONS = [
  'GBP', 'USD', 'EUR', 'AED', 'QAR', 'SAR', 'SGD', 'AUD', 'CAD', 'INR',
];

export const SALARY_PERIOD_OPTIONS = [
  { value: 'annual',  label: 'Annual' },
  { value: 'monthly', label: 'Monthly' },
];

export const ARRAY_FIELD_CAP        = 10;
export const EXCLUDED_KEYWORDS_CAP  = 20;

// ─── Comparison helpers ─────────────────────────────────────────

function sameList(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Compute the minimal patch that, applied to `server`, would yield
 * `form`. Returns an object containing only changed keys. Empty
 * object means nothing to save.
 */
export function diffPreferences(server, form) {
  const out = {};
  if (!server || !form) return out;
  for (const k of Object.keys(PREFERENCE_DEFAULTS)) {
    const s = server[k];
    const f = form[k];
    if (ARRAY_KEYS.includes(k)) {
      if (!sameList(s ?? [], f ?? [])) out[k] = f ?? [];
    } else if (s !== f) {
      // Includes null vs number transitions for salary bounds.
      out[k] = f;
    }
  }
  return out;
}

/**
 * Count the user-set preferences (i.e. those that diverge from
 * `PREFERENCE_DEFAULTS`). Empty arrays, default enum values, and
 * null salary bounds count as "not set". Returns an integer suitable
 * for "Filtering by N of your preferences".
 */
export function countActivePreferences(prefs) {
  if (!prefs || typeof prefs !== 'object') return 0;
  let count = 0;
  for (const k of Object.keys(PREFERENCE_DEFAULTS)) {
    const v = prefs[k];
    const def = PREFERENCE_DEFAULTS[k];
    if (ARRAY_KEYS.includes(k)) {
      if (Array.isArray(v) && v.length > 0) count += 1;
      continue;
    }
    if (k === 'salary_floor' || k === 'salary_ceiling') {
      // Either bound being a finite number counts as ONE salary
      // signal — but only count once across both bounds so a
      // floor+ceiling pair doesn't double-count.
      if (k === 'salary_floor') {
        const hasFloor   = typeof v === 'number';
        const hasCeiling = typeof prefs.salary_ceiling === 'number';
        if (hasFloor || hasCeiling) count += 1;
      }
      continue;
    }
    if (k === 'salary_currency' || k === 'salary_period') {
      // These never count alone — only meaningful with a bound.
      continue;
    }
    if (v != null && v !== def) count += 1;
  }
  return count;
}

/**
 * Build the same human-readable list the backend `format_candidate_
 * preferences_block` produces, but for client display (banner
 * tooltip / "what's active" expander). Returns an array of strings.
 */
export function describeActivePreferences(prefs) {
  if (!prefs || typeof prefs !== 'object') return [];
  const out = [];

  if (Array.isArray(prefs.preferred_titles) && prefs.preferred_titles.length) {
    out.push(`Preferred titles: ${prefs.preferred_titles.join(', ')}`);
  }
  if (Array.isArray(prefs.industries) && prefs.industries.length) {
    out.push(`Industries: ${prefs.industries.join(', ')}`);
  }
  if (Array.isArray(prefs.seniority_targets) && prefs.seniority_targets.length) {
    out.push(`Seniority: ${prefs.seniority_targets.join(', ')}`);
  }
  if (Array.isArray(prefs.employment_types) && prefs.employment_types.length) {
    out.push(`Employment types: ${prefs.employment_types.join(', ')}`);
  }
  if (prefs.remote_preferences && prefs.remote_preferences !== 'any') {
    out.push(`Remote: ${prefs.remote_preferences}`);
  }
  if (Array.isArray(prefs.preferred_regions) && prefs.preferred_regions.length) {
    out.push(`Locations: ${prefs.preferred_regions.join(', ')}`);
  }
  if (prefs.open_to_relocation && prefs.open_to_relocation !== 'conditional') {
    out.push(`Relocation: ${prefs.open_to_relocation}`);
  }
  if (prefs.location_strictness && prefs.location_strictness !== 'balanced') {
    out.push(`Strictness: ${prefs.location_strictness}`);
  }
  const floor   = prefs.salary_floor;
  const ceiling = prefs.salary_ceiling;
  const cur     = prefs.salary_currency || 'GBP';
  const per     = prefs.salary_period   || 'annual';
  if (typeof floor === 'number' && typeof ceiling === 'number') {
    out.push(`Salary: ${floor.toLocaleString()}–${ceiling.toLocaleString()} ${cur} (${per})`);
  } else if (typeof floor === 'number') {
    out.push(`Salary: ≥ ${floor.toLocaleString()} ${cur} (${per})`);
  } else if (typeof ceiling === 'number') {
    out.push(`Salary: ≤ ${ceiling.toLocaleString()} ${cur} (${per})`);
  }
  if (Array.isArray(prefs.excluded_keywords) && prefs.excluded_keywords.length) {
    out.push(`Excluded: ${prefs.excluded_keywords.join(', ')}`);
  }
  return out;
}

/**
 * Coerce a salary input string to a non-negative integer or null.
 * Empty / blank → null. Negative or non-numeric → null. Used by
 * the editor to keep the state shape consistent with the backend
 * before POST.
 */
export function parseSalaryBound(raw) {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) {
    return Math.round(raw);
  }
  if (typeof raw === 'string') {
    const s = raw.trim().replace(/[, ]/g, '');
    if (!s) return null;
    const n = Number(s);
    if (Number.isFinite(n) && n >= 0) return Math.round(n);
  }
  return null;
}
